import React, {useCallback, useContext, useRef, useState} from 'react';
import Dropdown from '@bonprix/pattern-library/templates/dropdown/default';
import FilterGroupContent from '../FilterGroupContent.jsx';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import Messages from '@core/scripts/components/messages';
import {articleOverviewStaticContext} from '../../ArticleOverviewStatic.jsx';
import {Constants} from '../../view-constants';
import {useDispatch, useSelector} from 'react-redux';
import {loadOffFilter, loadOn, loadOnFilter, selectFilterLoadingbarStatus} from '../../LoaderSlice.jsx';
import {mergeSelectedFiltersWithParams, yieldToMain} from '../../ArticleOverviewHelper.js';
import {loadData} from '../../helper/data-loader';
import JSONParser from '@core/scripts/helper/json-parser';
import {useTrinity} from '../../hooks/use-trinity.js';

function FilterGroup({filterGroup, expandLeft, hits, preventFilterUpdate, filterSubmit}) {
    const {texts, filterAjaxUrl, shopId, filterRootShopId, currentSearchParams} =
        useContext(articleOverviewStaticContext);
    const filterLoading = useSelector(selectFilterLoadingbarStatus).status;

    const [currentHits, setCurrentHits] = useState(hits);
    const currentFilters = useRef(new Map());
    const dropdown = useRef(null);

    const {sendClickBasic} = useTrinity();

    const categoryFilterOnlyHasLinks = filterGroup.filters.length === 0 && filterGroup.links.length > 0;

    const dropdownClasses = getDropdownClasses();
    const buttonText = `${texts.filter_submit} (${currentHits})`;

    const dispatch = useDispatch();

    const submitButtonClick = useCallback(
        (event) => {
            event?.preventDefault();

            const submitButtonDisabled = dropdown.current.dropdownContent.getAttribute(
                Constants.DISABLE_ATTRIBUTE_NAME,
            );
            if (!submitButtonDisabled) {
                yieldToMain(() => dispatch(loadOn()));
                MessageBroker.publish(Messages.DROPDOWN.CLOSE);
                filterSubmit(currentFilters.current);
                currentFilters.current = new Map();
            }
        },
        [filterSubmit],
    );

    const openDropdown = useCallback(() => {
        sendClickBasic('open_filter', Constants.FILTER_VIEW_EVENT_VERSION_CONTROLGROUP);
        setCurrentHits(hits);
    }, [hits]);

    const filterOptionChange = useCallback(
        async (identifier, trackingId, clickedFilters, isRange = false) => {
            return await yieldToMain(async () => {
                const filterData = {
                    trackingId: trackingId,
                    clickedFilterOptions: clickedFilters,
                };

                currentFilters.current.set(identifier, filterData);

                const newHits = await reloadHits(currentFilters.current);
                setCurrentHits(newHits);

                if (!newHits && isRange) {
                    currentFilters.current.delete(identifier);
                }

                return newHits;
            });
        },
        [currentFilters.current, reloadHits],
    );

    return (
        <Dropdown
            ref={dropdown}
            onLabelClick={(aboutToBeOpened) => {
                if (aboutToBeOpened) {
                    openDropdown();
                }
            }}
            dropdownItemClass={dropdownClasses}
            dropdownName={filterGroup.name}
            visibleLabel={filterGroup.name}
            disabled={filterLoading}
            buttonText={buttonText}
            buttonOnClick={submitButtonClick}
            showButton={!categoryFilterOnlyHasLinks}
        >
            <FilterGroupContent
                filterGroup={filterGroup}
                filterOptionChange={filterOptionChange}
                preventFilterUpdate={preventFilterUpdate}
                dropdown={dropdown}
            />
        </Dropdown>
    );

    function getDropdownClasses() {
        const expandDirection = expandLeft ? 'left' : 'right';
        const buttonVisibility = categoryFilterOnlyHasLinks ? '' : '-with-button';
        const dropdownStyle = `expands-${expandDirection} ${buttonVisibility}`;

        const isColorOrTiledFilter = filterGroup.filters[0]?.color || filterGroup.filters[0]?.tiled;
        const customWidth = isColorOrTiledFilter ? '-custom-width' : '';

        return `filter-group ${dropdownStyle} ${customWidth}`;
    }

    function reloadHits(clickedFilters, categoryFilter) {
        dispatch(loadOnFilter());

        let shopIdFormatted;
        if (categoryFilter) {
            shopIdFormatted = `/${filterRootShopId.current}/`; //
        } else {
            shopIdFormatted = shopId.current ? `/${shopId.current}/` : '/';
        }

        const currentParamsCopy = mergeSelectedFiltersWithParams(clickedFilters, currentSearchParams.current);
        const filterAjaxRequest = filterAjaxUrl + shopIdFormatted + '?' + currentParamsCopy.toString();

        const newHitsContent = loadData(filterAjaxRequest);
        return newHitsContent.then((result) => {
            dispatch(loadOffFilter());
            if (result) {
                const modelJson = JSONParser.tryParseJSON(result.model);
                return modelJson.hits;
            }
        });
    }
}

export default React.memo(FilterGroup);
